import { logger } from '../utils/logger';
import React, { useEffect, useRef, useState } from 'react';
import { makeAuthenticatedRequest } from '../utils/api';
import { useNotifications } from '../contexts/NotificationContext';
import './DailyQuestPanel.css';

interface DailyQuest {
  id: number;
  name: string;
  description: string;
  icon: string;
  objectiveType: string;
  progress: number;
  targetCount: number;
  rewardGold: number;
  completed: boolean;
  claimed: boolean;
}

interface StreakInfo {
  currentStreak: number;
  bestStreak: number;
  checkedInToday: boolean;
}

interface DailyResponse {
  quests: DailyQuest[];
  streak: StreakInfo;
}

const DailyQuestPanel: React.FC = () => {
  const [data, setData] = useState<DailyResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [claimingId, setClaimingId] = useState<number | null>(null);
  const { addNotification } = useNotifications();
  const completedIdsRef = useRef<Set<number>>(new Set());
  const firstLoadRef = useRef(true);

  useEffect(() => {
    loadDaily();
  }, []);

  const loadDaily = async () => {
    try {
      const response = await makeAuthenticatedRequest('/daily');
      if (response.ok) {
        const result: DailyResponse = await response.json();

        if (!firstLoadRef.current) {
          for (const quest of result.quests) {
            if (quest.completed && !completedIdsRef.current.has(quest.id)) {
              addNotification({
                type: 'quest',
                title: 'Daily Quest Complete!',
                message: `${quest.name} - claim your reward on the Home screen`,
                icon: quest.icon
              });
            }
          }
        }
        firstLoadRef.current = false;
        completedIdsRef.current = new Set(result.quests.filter(q => q.completed).map(q => q.id));

        setData(result);
      }
    } catch (err) {
      logger.error('Failed to load daily quests:', err);
    } finally {
      setLoading(false);
    }
  };

  const claimReward = async (questId: number) => {
    setClaimingId(questId);
    try {
      const response = await makeAuthenticatedRequest(`/daily/claim/${questId}`, { method: 'POST' });
      if (response.ok) {
        const result = await response.json();
        addNotification({
          type: 'loot',
          title: 'Reward Claimed!',
          message: `+${result.goldAwarded} gold`,
          icon: '💰'
        });
        await loadDaily();
      } else {
        const err = await response.json().catch(() => ({ message: 'Failed to claim reward' }));
        addNotification({ type: 'info', title: 'Could not claim', message: err.message || 'Failed to claim reward' });
      }
    } catch (err) {
      logger.error('Failed to claim daily quest reward:', err);
    } finally {
      setClaimingId(null);
    }
  };

  if (loading) {
    return (
      <div className="daily-quest-panel">
        <h3>Daily Quests</h3>
        <p>Loading...</p>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="daily-quest-panel">
      <h3>Daily Quests</h3>

      <div className="daily-streak">
        <span className="streak-icon">🔥</span>
        <span className="streak-label">Login Streak:</span>
        <span className="streak-value">{data.streak.currentStreak} {data.streak.currentStreak === 1 ? 'day' : 'days'}</span>
        {data.streak.bestStreak > data.streak.currentStreak && (
          <span className="streak-best">Best: {data.streak.bestStreak}</span>
        )}
      </div>

      <div className="daily-quest-list">
        {data.quests.map(quest => {
          const pct = Math.min(100, Math.round((quest.progress / quest.targetCount) * 100));
          return (
            <div key={quest.id} className={`daily-quest-item ${quest.claimed ? 'claimed' : quest.completed ? 'ready' : ''}`}>
              <span className="daily-quest-icon">{quest.icon}</span>
              <div className="daily-quest-info">
                <div className="daily-quest-name">{quest.name}</div>
                <div className="daily-quest-bar-track">
                  <div className="daily-quest-bar-fill" style={{ width: `${pct}%` }} />
                </div>
                <div className="daily-quest-meta">
                  <span>{quest.progress} / {quest.targetCount}</span>
                  <span className="daily-quest-reward">💰 {quest.rewardGold}</span>
                </div>
              </div>
              {quest.claimed ? (
                <span className="daily-quest-claimed-badge">Claimed</span>
              ) : quest.completed ? (
                <button
                  className="daily-quest-claim-btn"
                  disabled={claimingId === quest.id}
                  onClick={() => claimReward(quest.id)}
                >
                  {claimingId === quest.id ? '...' : 'Claim'}
                </button>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DailyQuestPanel;
